import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import AreaChartComp from "../Area/AreaChartComp";
import { sortData } from "../Utils/SortData";
import { lineTransformData } from "../../VizComponents/Utils/lineTransformData";
import { formatNumber } from "../Utils/FormatNumbers";
import { buildQuery } from "../../../features/reduxworkbook/utils/buildQuery";
import { useGetDataMutation } from "../../../features/reduxworkbook/dataApiSlice";
import { calculateNumber } from "./calculateNumber";
import LoadingIndicator from "../../BasicComponents/LoadingIndicator";
import useAuth from "../../../hooks/useAuth";

const checkIfExternalTotalRequired = (
  valueKeys,
  rawGroupByKeys2 = [],
  filters,
  commonFilters,
  dataSourceID
) => {
  if (
    !Array.isArray(valueKeys) ||
    valueKeys.length === 0 ||
    rawGroupByKeys2.length === 0
  )
    return { externalTotalRequired: false };

  const measureValue = valueKeys[0]?.measure?.value;
  if (!measureValue) return false;

  const measuresForBackendTotals = ["AVG", "DISTINCT"];
  const externalTotalRequired = measuresForBackendTotals.includes(measureValue);
  if (externalTotalRequired) {
    const queryObject = { valueColumns: valueKeys, filters };
    const { queryBody } = buildQuery(queryObject, dataSourceID, commonFilters);

    return { externalTotalRequired, queryBody };
  }

  return { externalTotalRequired };
};

const BigNumberComp = ({
  Data,
  valueKeys,
  Sheetid,
  rowKeys: rawRowKeys,
  groupByKeys: rawGroupByKeys,
  toolTipKeys,
  sortProperties,
  isSheetInDashboard,
  filters,
  commonFilters,
  dataSourceID,
}) => {
  console.log(valueKeys);

  const [getData, { data: totalData, isLoading, isSuccess, isError, error }] =
    useGetDataMutation();

  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );

  const { externalTotalRequired, queryBody } = useMemo(
    () =>
      checkIfExternalTotalRequired(
        valueKeys,
        rawGroupByKeys,
        filters,
        commonFilters,
        dataSourceID
      ),
    [valueKeys, rawGroupByKeys, filters, commonFilters, dataSourceID]
  );
  console.log(externalTotalRequired);
  console.log(queryBody);

  // Fetch external total data if required
  useEffect(() => {
    if (externalTotalRequired) {
      console.log(externalTotalRequired);
      console.log(queryBody);
      getData(queryBody);
    }
  }, [externalTotalRequired, getData, queryBody]);

  let {
    imageUrl,
    isIconVisible,
    isNumberVisible,
    isLabelVisible,
    imageWidth: imageSize,
    imageHeight,
    label,
    dashboardBoxSize,
    isNumberBold,
    isLabelBold,
    numberFontSize,
    numberFontColor,
    labelFontSize,
    labelFontColor,
    labelAlignment,
  } = customizationObject;
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const updateSize = () => {
      if (isSheetInDashboard && dashboardBoxSize) {
        setDimensions({
          width: dashboardBoxSize.width,
          height: dashboardBoxSize.height,
        });
      } else {
        if (containerRef.current) {
          setDimensions({
            width: containerRef.current.offsetWidth,
            height: containerRef.current.offsetHeight,
          });
        }
      }
    };

    updateSize(); // Initial update
    window.addEventListener("resize", updateSize); // Update on window resize

    return () => window.removeEventListener("resize", updateSize);
  }, [dashboardBoxSize]);
  const imageWidth = (dimensions.width * imageSize) / 80;
  imageHeight = dimensions.height;
  labelFontSize = isSheetInDashboard
    ? (dimensions.width * labelFontSize) / 1100
    : (dimensions.width * labelFontSize) / 1000;
  numberFontSize = isSheetInDashboard
    ? (dimensions.width * numberFontSize) / 500
    : (dimensions.width * numberFontSize) / 1000;

  function setOrder(labelAlignment) {
    let order = { numberOrder: 2, labelOrder: 1 };
    if (labelAlignment) {
      if (labelAlignment.value === "top") {
        order = { numberOrder: 2, labelOrder: 1 };
      } else if (labelAlignment.value === "bottom") {
        order = { numberOrder: 1, labelOrder: 2 };
      }
    } else {
      order = { numberOrder: 2, labelOrder: 1 };
    }
    return order;
  }

  const orders = setOrder(labelAlignment);
  console.log(orders);
  const { numberOrder, labelOrder } = orders;
  const number = useMemo(
    () =>
      calculateNumber(
        Data,
        valueKeys,
        rawGroupByKeys,
        externalTotalRequired,
        totalData,
        isSuccess
      ),
    [
      Data,
      valueKeys,
      rawGroupByKeys,
      externalTotalRequired,
      totalData,
      isSuccess,
    ]
  );
  console.log(number);

  const { table: transformedData, lines: uniqueColumnValues } = useMemo(() => {
    return lineTransformData(
      Data,
      rawRowKeys,
      rawGroupByKeys,
      valueKeys,
      toolTipKeys
    );
  }, [Data, rawGroupByKeys, rawRowKeys, valueKeys, toolTipKeys]);
  const sorteData = sortData({
    transformedData,
    sortProperties,
    requiredArray: rawGroupByKeys,
  });

  const numberObject = valueKeys[0]?.numberFormat;

  if (externalTotalRequired && isLoading) {
    return <LoadingIndicator />;
  }
  if (externalTotalRequired && isError) {
    return (
      <main>
        Error while fetching totals: {error?.data?.error || "An error occurred"}
      </main>
    );
  }
  return (
    <section
      className="w-full h-full flex flex-col items-start justify-between gap-2"
      ref={containerRef}
    >
      <div className="flex items-center justify-center bg-white w-full h-full">
        <div className="w-full h-full flex items-center justify-start gap-x-2">
          {isIconVisible && imageUrl ? (
            <div className="flex flex-col items-center justify-center h-full">
              <img
                src={imageUrl}
                alt="Image"
                className="object-contain"
                style={{ width: imageWidth, height: imageHeight }}
              />
            </div>
          ) : null}
          <div className="w-auto flex flex-col gap-0 items-start justify-center overflow-hidden truncate h-full">
            {/* Number */}
            {isNumberVisible && number && (
              <div
                className={`w-auto ${isNumberBold ? "font-bold" : ""}`}
                style={{
                  fontSize: numberFontSize,
                  color: numberFontColor ?? "#000000",
                  order: numberOrder,
                }}
              >
                {formatNumber(number, numberObject)}
              </div>
            )}

            {/* Label */}
            {isLabelVisible && (
              <div
                className={`w-auto ${isLabelBold ? "font-bold" : ""}`}
                style={{
                  fontSize: labelFontSize,
                  color: labelFontColor ?? "#000000",
                  order: labelOrder,
                }}
              >
                {label || "your text here..."}
              </div>
            )}
          </div>
        </div>
      </div>
      {rawGroupByKeys.length === 1 ? (
        <div className="w-full items-center justify-center flex h-[30%]">
          <AreaChartComp
            hideXAxis={true}
            hideYAxis={true}
            hiddenLabels={true}
            Data={sorteData}
            Sheetid={Sheetid}
            rowKeys={rawRowKeys}
            valueKeys={valueKeys}
            groupByKeys={rawGroupByKeys}
            toolTipKeys={toolTipKeys}
            isAreaInBignumberChart={true}
            sortProperties={sortProperties}
          />
        </div>
      ) : null}
    </section>
  );
};

export default BigNumberComp;
